import { useQuery, UseQueryResult } from "@tanstack/react-query";
import config from "../config.json";
import { IPageable, Option, Params } from "../types/table/interfaces.type";
import { createAxiosInstance } from "./ServicesHelpers";

const axiosInstance = createAxiosInstance(config.ADMIN_API_BASE_URL);

export type VpnProtocol = "IPSEC_IKE";
export type VpnTunnelStatus = "UP" | "DOWN" | "CONNECTING";

export interface IVpnTunnel {
  id: number;
  version?: number;
  connectionName: string;
  protocol: VpnProtocol;
  remoteGateway: string;
  remoteSubnets: string;
  localSubnets?: string;
  localNatIp?: string;
  localId?: string;
  remoteId?: string;
  ikeProposal: string;
  ikeLifetimeSeconds: number;
  espProposal: string;
  saLifetimeSeconds: number;
  saLifetimeKbytes?: number;
  pfsEnabled: boolean;
  isInitiator?: boolean;
  psk?: string;
  description?: string;
  createdDate?: string;
  timestamp?: string;
  createdBy?: string;
  updatedBy?: string;
  historyTimestamp?: string;
}

export interface IVpnTunnelStatusResponse {
  connectionName: string;
  protocol: VpnProtocol;
  status: VpnTunnelStatus;
  remoteGateway: string;
  remoteSubnets: string | null;
  errorMessage: string | null;
  connectedSince: string | null;
}

export interface IVpnTunnelDiagnostics {
  connectionName: string;
  ipsecVersion: string;
  configFile: string;
  statusOutput: string;
  trafficStatus: string;
  errorMessage: string | null;
}

export const fetchVpnTunnels = async (
  params: Params<IVpnTunnel>
): Promise<IPageable<IVpnTunnel>> => {
  try {
    const response = await axiosInstance.get("/vpn-tunnels", {
      params: { ...params, search: params.search ?? "" },
    });
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while fetching VPN tunnels."
    );
  }
};

export const fetchVpnTunnelsAll = async (): Promise<IVpnTunnel[]> => {
  try {
    const response = await axiosInstance.get("/vpn-tunnels/all");
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while fetching VPN tunnels."
    );
  }
};

export const getVpnTunnelById = async (
  id: number | string
): Promise<IVpnTunnel> => {
  try {
    const response = await axiosInstance.get(`/vpn-tunnels/${id}`);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while fetching the VPN tunnel."
    );
  }
};

export const addVpnTunnelApi = async (
  data: Partial<IVpnTunnel>
): Promise<IVpnTunnel> => {
  try {
    const response = await axiosInstance.post("/vpn-tunnels", data);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while creating the VPN tunnel."
    );
  }
};

export const editVpnTunnelApi = async (
  id: number | string,
  data: Partial<IVpnTunnel>
): Promise<IVpnTunnel> => {
  try {
    const response = await axiosInstance.put(`/vpn-tunnels/${id}`, data);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while updating the VPN tunnel."
    );
  }
};

export const deleteVpnTunnelApi = async (id: number | string): Promise<void> => {
  try {
    await axiosInstance.delete(`/vpn-tunnels/${id}`);
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while deleting the VPN tunnel."
    );
  }
};

export const connectVpnTunnelApi = async (
  id: number | string
): Promise<IVpnTunnelStatusResponse> => {
  try {
    const response = await axiosInstance.post(`/vpn-tunnels/${id}/connect`);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while connecting the VPN tunnel."
    );
  }
};

export const disconnectVpnTunnelApi = async (
  id: number | string
): Promise<IVpnTunnelStatusResponse> => {
  try {
    const response = await axiosInstance.post(`/vpn-tunnels/${id}/disconnect`);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while disconnecting the VPN tunnel."
    );
  }
};

export const fetchVpnTunnelStatusesApi = async (): Promise<
  IVpnTunnelStatusResponse[]
> => {
  try {
    const response = await axiosInstance.get("/vpn-tunnels/statuses");
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while fetching VPN tunnel statuses."
    );
  }
};

export const fetchVpnTunnelDiagnosticsApi = async (
  id: number | string
): Promise<IVpnTunnelDiagnostics> => {
  try {
    const response = await axiosInstance.get(`/vpn-tunnels/${id}/diagnostics`);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while fetching VPN tunnel diagnostics."
    );
  }
};

export async function fetchVpnTunnelHistory(
  id: IVpnTunnel["id"],
  params: Params<IVpnTunnel> = {},
): Promise<IPageable<IVpnTunnel>> {
  try {
    const response = await axiosInstance.get("/vpn-tunnels/history", {
      params: { mainId: id, sort: "historyTimestamp,desc", ...params },
    });
    const data = response.data;
    return {
      ...data,
      content: data.content.map((item: any) => ({
        ...item.snapshot,
        historyTimestamp: item.historyTimestamp,
      })),
    };
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        `An error occurred while getting VPN tunnel history for ID ${id}.`,
    );
  }
}

export const useVpnTunnelsQuery = (
  params: Params<IVpnTunnel>
): UseQueryResult<IPageable<IVpnTunnel>, Error> => {
  const effectiveParams = {
    ...params,
    sort: params.sort || "createdDate,desc",
  };
  const queryKey = ["vpn-tunnels", JSON.stringify(effectiveParams)];
  return useQuery({
    queryKey,
    queryFn: () => fetchVpnTunnels(effectiveParams),
  });
};

export const useVpnTunnelsAllQuery = (): UseQueryResult<Option[], Error> => {
  return useQuery({
    queryKey: ["vpn-tunnels-all"],
    queryFn: async () => {
      try {
        const tunnels = await fetchVpnTunnelsAll();
        return tunnels.map((tunnel) => ({
          value: tunnel.id,
          label: `${tunnel.connectionName} - ${tunnel.remoteGateway}`,
        }));
      } catch {
        // VPN tunnel is optional for accounts; return empty on 403/other errors
        return [];
      }
    },
    retry: false,
  });
};
